import { Node, mergeAttributes } from '@tiptap/core';
import {
  ReactNodeViewRenderer,
  NodeViewWrapper,
  NodeViewContent,
} from '@tiptap/react';

// A dedicated FAQ block: an accordion of question/answer pairs that renders to a
// predictable data-type structure. The reader (folge-cloud) turns this markup into
// native <details> accordions and emits FAQPage JSON-LD from it.
//
// Storage markup (from renderHTML), which becomes the article's content_html:
//   <div data-type="faq-section">
//     <div data-type="faq-item">
//       <div data-type="faq-question">Question</div>
//       <div data-type="faq-answer"><p>Answer</p></div>
//     </div>
//   </div>

// ---- Node schema ----------------------------------------------------------

const FaqSection = Node.create({
  name: 'faqSection',
  group: 'block',
  content: 'faqItem+',
  defining: true,

  parseHTML() {
    return [{ tag: 'div[data-type="faq-section"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-type': 'faq-section', class: 'faq-section' }),
      0,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(FaqSectionView);
  },
});

const FaqItem = Node.create({
  name: 'faqItem',
  content: 'faqQuestion faqAnswer',
  defining: true,
  isolating: true,

  parseHTML() {
    return [{ tag: 'div[data-type="faq-item"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-type': 'faq-item', class: 'faq-item' }),
      0,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(FaqItemView);
  },
});

const FaqQuestion = Node.create({
  name: 'faqQuestion',
  content: 'inline*',
  marks: '',
  defining: true,
  isolating: true,

  parseHTML() {
    return [{ tag: 'div[data-type="faq-question"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-type': 'faq-question', class: 'faq-question' }),
      0,
    ];
  },
});

const FaqAnswer = Node.create({
  name: 'faqAnswer',
  content: 'block+',
  defining: true,
  isolating: true,

  parseHTML() {
    return [{ tag: 'div[data-type="faq-answer"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-type': 'faq-answer', class: 'faq-answer' }),
      0,
    ];
  },
});

// ---- Node views (editor UX only; do not affect stored HTML) ---------------

function newFaqItem() {
  return {
    type: 'faqItem',
    content: [
      { type: 'faqQuestion' },
      { type: 'faqAnswer', content: [{ type: 'paragraph' }] },
    ],
  };
}

function FaqSectionView(props: any) {
  const { editor, getPos, node } = props;

  const addItem = () => {
    if (typeof getPos !== 'function') return;
    const endPos = getPos() + node.nodeSize - 1; // just before the section closes
    editor.chain().focus().insertContentAt(endPos, newFaqItem()).run();
  };

  return (
    <NodeViewWrapper className="faq-section-wrapper">
      <NodeViewContent className="faq-section" />
      <button
        type="button"
        contentEditable={false}
        onClick={addItem}
        className="faq-add-btn"
      >
        + Add question
      </button>
    </NodeViewWrapper>
  );
}

function FaqItemView(props: any) {
  const { editor, getPos, node } = props;

  const remove = () => {
    if (typeof getPos !== 'function') return;
    const from = getPos();
    const to = from + node.nodeSize;
    const $from = editor.state.doc.resolve(from);
    const section = $from.parent;
    if (section?.type?.name === 'faqSection' && section.childCount <= 1) {
      // Removing the last item would leave an invalid empty section — drop the section.
      const sectionFrom = $from.before();
      editor
        .chain()
        .focus()
        .deleteRange({ from: sectionFrom, to: sectionFrom + section.nodeSize })
        .run();
    } else {
      editor.chain().focus().deleteRange({ from, to }).run();
    }
  };

  return (
    <NodeViewWrapper className="faq-item-wrapper">
      <button
        type="button"
        contentEditable={false}
        onClick={remove}
        className="faq-remove-btn"
        title="Remove question"
        aria-label="Remove question"
      >
        ×
      </button>
      <NodeViewContent className="faq-item" />
    </NodeViewWrapper>
  );
}

// Convenience: the content object to insert a fresh FAQ block.
export const FAQ_BLOCK_CONTENT = {
  type: 'faqSection',
  content: [newFaqItem()],
};

export const FaqExtensions = [FaqSection, FaqItem, FaqQuestion, FaqAnswer];
